import {
  DEFAULT_NEW_CUSTOMER_DATA,
  ReviewCustomerStep
} from './components/steps/ReviewCustomer'
import invariant from 'tiny-invariant'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { ReviewPricingStep } from './components/steps/ReviewPricing'
import { format, parseISO } from '@sequencehq/utils/dates'
import {
  parseBillingSchedulePhaseDuration,
  calculateAbsoluteDatesForPhases,
  isPriceUsageBased
} from '@sequencehq/utils'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { isSavedIdPair, toSavedId } from './utils/toSavedId'
import { generateDefaultPhase } from './components/steps/ReviewPricing/utils/generateDefaultPhase'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  customFieldsFromRecord,
  customFieldsToRecord
} from '@sequencehq/core-components'
import { CountriesAlpha2, statesForCountry } from '@sequencehq/api'
import {
  TaskInput,
  TaskInputPhase,
  TaskInputPrice,
  TaskOutput
} from '../../types'

/**
 * The LLM extraction can include states which we don't currently support or model.
 * This causes an error creating the customer, and we don't otherwise show the
 * data point within the task (due to the aforementioned lack of support).
 * Therefore, if the country has no states/provinces that we support, we strip it out of the input
 * to avoid errors downstream.
 * @param country
 * @returns
 */
export const shouldStripStateFromInput = (
  country: CountriesAlpha2 | undefined
) => !country || !statesForCountry(country)?.length

export const customerDataAdapter = {
  fromInput: (taskInput: TaskInput): ReviewCustomerStep['data'] => {
    const taskInputCustomer = taskInput.customer ?? {}

    return {
      customerType: taskInputCustomer.id ? 'existing' : 'new',
      existingCustomer: { id: taskInputCustomer.id ?? '' },
      newCustomer: {
        ...DEFAULT_NEW_CUSTOMER_DATA,
        ...taskInputCustomer,
        address: {
          ...DEFAULT_NEW_CUSTOMER_DATA.address,
          ...taskInputCustomer.address,
          state: shouldStripStateFromInput(taskInputCustomer.address?.country)
            ? undefined
            : taskInputCustomer.address?.state
        },
        contacts:
          taskInputCustomer.contacts?.map(contact => ({
            id: crypto.randomUUID(),
            name: contact.name ?? '',
            email: contact.email ?? '',
            purposes:
              contact.purposes ??
              ((taskInputCustomer.contacts?.length ?? 0) > 1
                ? []
                : ['BILLING']),
            notificationSubscriptions: contact.notificationSubscriptions ?? [
              'BILLING'
            ]
          })) ?? [],
        // the same alias can't be used between customers
        aliases: [],
        // The task input carries custom properties as a key-value map; the
        // editor works with an array of rows, so convert on the way in.
        customProperties: customFieldsFromRecord(
          taskInputCustomer.customProperties
        )
      }
    }
  },
  toOutput: async (stepData: ReviewCustomerStep['data']): Promise<string> => {
    if (stepData.customerType === 'existing') {
      return stepData.existingCustomer.id
    }

    invariant(stepData.newCustomer.address.country, 'country is undefined')
    invariant(stepData.newCustomer.taxStatus, 'taxStatus is undeifned')

    // Tax registrations are persisted via separate calls below, not on the
    // customer body.
    const { taxRegistrations, ...newCustomerBody } = stepData.newCustomer

    const customerRes = await dashboardv99990101Client.postCustomer({
      body: {
        ...newCustomerBody,
        address: {
          ...stepData.newCustomer.address,
          country: stepData.newCustomer.address.country
        },
        taxStatus: stepData.newCustomer.taxStatus,
        contacts: stepData.newCustomer.contacts.map(contact => ({
          name: contact.name,
          email: contact.email,
          purposes: contact.purposes,
          notificationSubscriptions: contact.notificationSubscriptions
        })),
        customerAliases: stepData.newCustomer.aliases.filter(Boolean),
        customProperties: customFieldsToRecord(
          stepData.newCustomer.customProperties
        )
      }
    })

    if (customerRes.error || !customerRes.data) {
      throw new Error('Unable to save new customer')
    }
    /**
     * Because transactions are for nerds, I guess we now have to go do a separate call to save
     * the tax registration details. Fun! Each registration is a separate call.
     */
    const registrationsToSave = taxRegistrations.filter(
      registration => registration.identifier.trim() && registration.country
    )

    const taxResults = await Promise.all(
      registrationsToSave.map(registration =>
        dashboard20240730Client.postTaxRegistration({
          body: {
            customerId: customerRes.data?.id ?? '',
            taxIdentifier: registration.identifier,
            country: registration.country as CountriesAlpha2,
            // Only US/CA support a state/province; sending it otherwise is rejected.
            state: shouldStripStateFromInput(
              registration.country as CountriesAlpha2
            )
              ? undefined
              : registration.state
          }
        })
      )
    )

    if (taxResults.some(taxRes => taxRes.error || !taxRes.data)) {
      /**
       * Because of aforementioned transaction nerdiness, we can't really roll back the customer creation
       * if the tax registration fails. So we'll just log it and move on so we can at least detect
       * this in Posthog in the event something goes wrong.
       *
       * Sad.
       */
      console.warn('Failed to create tax registration for customer')
    }

    return customerRes.data.id
  }
}

export const sanitizePhaseDiscounts = (args: { phase: TaskInputPhase }) => {
  return args.phase.discounts
    .map((discount, idx) => {
      const sanitizedRestrictToPrices = discount.restrictToPrices ?? []
      const sanitizedPrices = sanitizedRestrictToPrices.filter(priceId => {
        const price = args.phase.priceIds?.includes(priceId)

        if (!price) {
          console.warn(
            `[Discount sanitization] Could not find price with temporaryPriceId ${priceId}. Removing from discount.`
          )
          return false
        }

        return true
      })

      /**
       * If the discount was restricted to prices, but we filtered out all of those prices,
       * then we should filter out the discount too
       */
      if (!sanitizedPrices.length && sanitizedRestrictToPrices.length) {
        console.warn(
          `[Discount sanitization] All prices were removed from restrictToPrices. Removing discount.`
        )
        return null
      }

      return {
        ...discount,
        id: crypto.randomUUID(),
        boundingBoxIndex: idx,
        restrictToPrices: sanitizedRestrictToPrices.filter(priceId =>
          args.phase.priceIds?.find(pid => pid === priceId)
        )
      }
    })
    .filter((d): d is NonNullable<typeof d> => Boolean(d))
}

export const sanitizePhaseMinimums = (args: {
  prices: Array<TaskInputPrice>
  phase: TaskInputPhase
}) => {
  return args.phase.minimums
    .map((minimum, idx) => {
      const sanitizedPrices = minimum.restrictToPrices.filter(priceId => {
        const priceFound = args.phase.priceIds?.includes(priceId)

        if (!priceFound) {
          console.warn(
            `[Minimum sanitization] Could not find price with temporaryPriceId ${priceId}. Removing from minimum.`
          )
          return false
        }

        const price = args.prices.find(p => p.temporaryPriceId === priceId)

        if (!price || !isPriceUsageBased(price)) {
          console.warn(
            `[Minimum sanitization] Price with temporaryPriceId ${priceId} is not usage-based. Removing from minimum.`
          )
          return false
        }
        return true
      })
      /**
       * If the minimum was restricted to prices, but we filtered out all of those prices,
       * then we should filter out the minimum too
       */
      if (!sanitizedPrices.length && minimum.restrictToPrices.length) {
        console.warn(
          `[Minimum sanitization] All prices were removed from restrictToPrices. Removing minimum.`
        )
        return null
      }

      return {
        ...minimum,
        id: crypto.randomUUID(),
        boundingBoxIndex: idx,
        restrictToPrices: sanitizedPrices
      }
    })
    .filter((m): m is NonNullable<typeof m> => Boolean(m))
}

export const billingScheduleAdapter = {
  fromInput: (
    taskInput: TaskInput,
    additionalData: {
      merchantDefaultDueDateDays?: number
    }
  ): ReviewPricingStep['data'] => {
    const inputPhases = taskInput.billingSchedule?.phases
    const inputPrices = taskInput.billingSchedule?.prices
    const inputSchedule = taskInput.billingSchedule ?? {}

    return {
      startDate: inputPhases?.[0]?.startDate
        ? parseISO(inputPhases[0].startDate)
        : new Date(),
      phases: inputPhases?.map((phase, idx) => {
        return {
          ...phase,
          recurrencePreference: phase.recurrencePreference ?? 'RESET',
          boundingBoxIndex: idx,
          phasePriceMetadata: phase.phasePriceMetadata ?? [],
          id: crypto.randomUUID(),
          duration: parseBillingSchedulePhaseDuration({
            startDate: phase.startDate ? parseISO(phase.startDate) : undefined,
            endDate: phase.endDate ? parseISO(phase.endDate) : undefined
          }),
          priceIds: phase.priceIds.map(priceId => {
            return (
              inputPrices?.find(p => p.temporaryPriceId === priceId)
                ?.temporaryPriceId ?? ''
            )
          }),
          discounts: sanitizePhaseDiscounts({ phase }),
          minimums: sanitizePhaseMinimums({
            phase,
            prices: inputPrices ?? []
          })
        }
      }) ?? [generateDefaultPhase()],
      prices:
        inputPrices?.map((price, idx) => {
          return {
            ...price,
            productId: undefined,
            id: price.temporaryPriceId,
            boundingBoxIndex: idx,
            /**
             * These can be missing, which would not match up to the data expectations
             * of the type otherwise. This lead to a crash when loading a price, missing
             * these fields, when an integration was active.
             */
            integrationIds: price.integrationIds ?? [],
            customMetricParameters: price.customMetricParameters ?? []
          }
        }) ?? [],
      recurrenceDayOfMonth: inputSchedule.recurrenceDayOfMonth ?? 1,
      purchaseOrderNumber: inputSchedule.purchaseOrderNumber ?? '',
      label: inputSchedule.label ?? '',
      memo: inputSchedule.memo ?? '',
      defaultDueDateDays:
        inputSchedule.defaultDueDateDays ??
        additionalData.merchantDefaultDueDateDays
    }
  },
  toOutput: async (
    stepData: ReviewPricingStep['data']
  ): Promise<TaskOutput['billingSchedule']> => {
    const pricesUsedInPhases = stepData.prices.filter(price =>
      stepData.phases.some(phase => phase.priceIds.includes(price.id))
    )

    const pricesToSave = pricesUsedInPhases.map(async price => {
      invariant(price.productId, 'Product must be defined to save the price')
      const res = await dashboardv20240509Client.postPrice({
        body: {
          ...price,
          productId: price.productId,
          integrationIds: [],
          customMetricParameters: []
        }
      })

      if (res.error || !res.data) return

      return {
        temporaryId: price.id,
        savedId: res.data.id
      }
    })

    const savedPrices = await Promise.all(pricesToSave)
    const priceIdPairs = savedPrices.filter(isSavedIdPair)

    const phases = calculateAbsoluteDatesForPhases({
      phases: stepData.phases,
      startDate: stepData.startDate
    })

    const billingScheduleStart = phases.at(0)?.startDate
    const billingScheduleEnd = phases.at(-1)?.endDate

    invariant(billingScheduleStart, 'billingScheduleStart is undefined')

    return {
      phases: phases.map(phase => {
        return {
          ...phase,
          startDate: format(phase.startDate, 'yyyy-MM-dd'),
          endDate: phase.endDate
            ? format(phase.endDate, 'yyyy-MM-dd')
            : undefined,
          priceIds: phase.priceIds.map(temporaryPriceId => {
            return toSavedId(priceIdPairs)(temporaryPriceId)
          }),
          discounts: phase.discounts.map(discount => {
            return {
              amount: discount.amount,
              discountCalculationType: discount.type,
              priceIds: discount.restrictToPrices.map(temporaryPriceId => {
                return toSavedId(priceIdPairs)(temporaryPriceId)
              }),
              message: discount.message,
              seatDiscountType: discount.seatDiscountType,
              id: discount.id
            }
          }),
          minimums: phase.minimums.map(minimum => {
            return {
              id: minimum.id,
              amount: minimum.amount,
              restrictToPrices: minimum.restrictToPrices.map(
                temporaryPriceId => {
                  return toSavedId(priceIdPairs)(temporaryPriceId)
                }
              )
            }
          }),
          phasePriceMetadata: phase.phasePriceMetadata.map(metadata => {
            return {
              ...metadata,
              priceId: toSavedId(priceIdPairs)(metadata.priceId)
            }
          })
        }
      }),
      prices: pricesUsedInPhases.map(price => {
        return {
          ...price,
          /**
           * This will be defined by the time it hits here
           */
          productId: price.productId as string,
          id: toSavedId(priceIdPairs)(price.id),
          usageCalculationPeriod: undefined
        }
      }),
      recurrenceDayOfMonth: stepData.recurrenceDayOfMonth,
      purchaseOrderNumber: stepData.purchaseOrderNumber,
      label: stepData.label,
      memo: stepData.memo,
      defaultDueDateDays: stepData.defaultDueDateDays,
      billingScheduleStartDate: format(billingScheduleStart, 'yyyy-MM-dd'),
      billingScheduleEndDate: billingScheduleEnd
        ? format(billingScheduleEnd, 'yyyy-MM-dd')
        : undefined
    }
  }
}
